import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { RootState } from '../../../ducks/configureStore';
import { findBaseResource } from '../../../utils/slide';
import TextLoader from '../../SlideAssets/TextLoader';
import { WindowSizes } from '../../../utils/constants';

interface IProps {
  windowSize: WindowSizes;
  hasLandscapeDoc: boolean;
  hasPortraitDoc: boolean;
}

const ExplainerPanels = ({ windowSize, hasLandscapeDoc, hasPortraitDoc }: IProps) => {
  // REDUX STATE
  const { timeline, panels, showCollapsiblePanel } = useSelector((state: RootState) => {
    const { slidesLitmos, quizLitmos } = state;
    const { currentSlide } = slidesLitmos;
    const { showCollapsiblePanel } = quizLitmos;
    const { timeline, panels } = currentSlide || {};

    return {
      timeline,
      panels,
      showCollapsiblePanel,
    };
  });

  const renderExplainerPanel = (panel: Panel, index: number, panelTimeline: Annotation[]) => {
    const numberOfPanels = panels?.length || 0;

    // This is a collapsible panel if it is the first in a list of at least 2 panels.
    const collapsible = numberOfPanels && numberOfPanels > 1 && index === 0;

    const resource = findBaseResource(panelTimeline);
    const main =
      (numberOfPanels && numberOfPanels > 1 && index > 0) || (numberOfPanels === 1 && index === 0);
    if (collapsible) {
      return (
        <CSSTransition
          in={!!showCollapsiblePanel}
          timeout={300}
          classNames="collapsible-explainer-anim"
        >
          <div
            aria-hidden={true} // Hide Takeaway panels from screen readers
            className={classNames('ExplainerPanel-wrapper', windowSize, {
              collapsible,
              collapsed: !showCollapsiblePanel,
              expanded: showCollapsiblePanel,
              main,
              solo: numberOfPanels === 1,
              'has-landscape-doc': hasLandscapeDoc,
              'has-portrait-doc': hasPortraitDoc,
            })}
          >
            <div
              key={index}
              className={classNames('ExplainerPanel', windowSize, {
                collapsible,
                collapsed: !showCollapsiblePanel,
                expanded: showCollapsiblePanel,
                main,
              })}
            >
              {/* Panel Proper */}
              <TextLoader
                key={resource?._id}
                resource={resource}
                panelId={panel._id}
                showCollapsiblePanel={showCollapsiblePanel}
                windowSize={windowSize}
                // Explainers won't have a supplemental doc
                hasSupplementalDoc={false}
                isMainPanel={main}
              />
            </div>
          </div>
        </CSSTransition>
      );
    }
    return (
      <div
        className={classNames('ExplainerPanel-wrapper', windowSize, {
          collapsible,
          collapsed: !showCollapsiblePanel,
          expanded: showCollapsiblePanel,
          main,
          solo: numberOfPanels === 1,
          'has-landscape-doc': hasLandscapeDoc,
          'has-portrait-doc': hasPortraitDoc,
        })}
      >
        <div
          key={index}
          className={classNames('ExplainerPanel', windowSize, {
            collapsible,
            main,
          })}
        >
          {/* Panel Proper */}
          <TextLoader
            key={resource?._id}
            resource={resource}
            panelId={panel._id}
            showCollapsiblePanel={showCollapsiblePanel}
            windowSize={windowSize}
            // Explainers won't have a supplemental doc
            hasSupplementalDoc={false}
            isMainPanel={main}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {panels?.map((panel, index) => {
        const panelTimeline = timeline?.filter(
          (timelineElement) => timelineElement.panelId === panel._id,
        );
        if (panelTimeline) {
          return renderExplainerPanel(panel, index, panelTimeline);
        }
        return null;
      })}
    </>
  );
};

export default ExplainerPanels;
